import React, { useCallback, useEffect, useRef, useState } from 'react';
import automationService from '../../services/automationService';
import LoadingSpinner from '../../components/LoadingSpinner';
import Button from '../../ui/Button/Button';
import Card from '../../ui/Card';
import Badge from '../../ui/Badge';
import StatusBadge from '../../ui/Status/StatusBadge';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import { Input, Select } from '../../ui/Field/Field';
import { formatPlateNumber } from '../../utils/plateFormatter';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import { confirmDialog } from '../../ui/feedback';
import { CellMuted } from '../../ui/DataTable/cells';

// Every Ejar contract is registered on TARS (owner, 10 Oct 2026: "اى عقد لازم يتسجل على تارز"). The engine signs in to
// TARS itself and fills TARS's own five screens from Ejar - the car, the customer, the contract with its picture
// printed from Ejar on the company paper, the drivers, the summary - and stops there: Terms on, Submit NOT pressed
// until the owner approves the whole trial ("لغاية ما نخلص التجربة كامله ميدوسش سبميت").

const BRANCH = { 36: 'Airport T1', 174: 'Airport T2', 192: 'Business Bay', 1502: 'Invygo' };
const RUNNING = ['preparing', 'filling', 'needs_customer'];
const LIVE = [...RUNNING, 'ready'];             // a job still open on the engine
const GONE = ['cancelled', 'done', 'already', 'expired'];   // ended well or on purpose: the card leaves by itself
// TARS's last button already pressed: nothing to cancel any more, and a failure after it is kept on the card
const pressed = (j) => /pressing|pressed|WAS PRESSED/.test(`${j?.step || ''} ${j?.error || ''}`) && j?.state !== 'done';
// the job survives a tab switch (sessionStorage, this browser only) - one per tab: registering, closing
const keep = (mode) => (mode === 'close' ? 'yelo.tars.close.job' : 'yelo.tars.job');
const remember = (mode, id) => { try { if (id) sessionStorage.setItem(keep(mode), id); else sessionStorage.removeItem(keep(mode)); } catch (e) { /* private window */ } };
const remembered = (mode) => { try { return sessionStorage.getItem(keep(mode)); } catch (e) { return null; } };
const JOB_TONE = {
    preparing: 'info', filling: 'info', needs_customer: 'warning', ready: 'success', done: 'success', already: 'neutral',
    stopped: 'warning', failed: 'danger', cancelled: 'neutral', expired: 'neutral',
};
const JOB_WORDS = {
    preparing: 'preparing from Ejar', filling: 'filling TARS', needs_customer: 'new customer - details needed', ready: 'filled - waiting at the summary (Submit not pressed)',
    done: 'registered on TARS',
    already: 'already on TARS', stopped: 'stopped', failed: 'failed', cancelled: 'cancelled', expired: 'expired',
};

const kb = (n) => `${Math.max(1, Math.round((n || 0) / 1024))} KB`;

// A customer TARS does not know: its new-customer form filled from Ejar's profile and scans. What Ejar lacks - a
// tourist's visit visa (a paper only, never typed), an English name Claude could not read - is asked for here.
const NewCustomer = ({ job, onJob }) => {
    const c = job.customer;
    const waiting = job.state === 'needs_customer';
    const [values, setValues] = useState({});
    const [papers, setPapers] = useState([]);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => { setValues({}); setPapers([]); }, [job.job]);
    if (!c) return null;
    const missing = new Set(c.missing || []);
    const value = (f) => (values[f.id] !== undefined ? values[f.id] : f.value || '');
    const send = async () => {
        setSending(true); setError(null);
        try {
            onJob(await automationService.tarsRegisterCustomer(job.job, values, papers));
            setValues({}); setPapers([]);
        } catch (e) { setError(e.message); }
        finally { setSending(false); }
    };
    return (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50/40 p-3">
            <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
                <b className="text-gray-900">New customer on TARS · {c.form} form</b>
                {waiting && !!missing.size && (
                    <Badge variant="warning">needed: {[...missing].map((m) => (c.fields.find((f) => f.id === m)?.label || m)).join(', ')}</Badge>
                )}
            </div>
            {!!(c.notes || []).length && (
                <ul className="mb-2 list-disc ps-5 text-xs text-gray-700">{c.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
            )}
            <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                {c.fields.map((f) => (
                    <label key={f.id} className="text-xs text-gray-700">
                        {f.label}{f.required && <span className="text-red-600"> *</span>}
                        {waiting ? (
                            <Input type={f.date ? 'date' : 'text'} size="sm" value={value(f)}
                                className={missing.has(f.id) ? 'border-red-400' : ''}
                                onChange={(e) => setValues({ ...values, [f.id]: e.target.value })} />
                        ) : <div className="mt-0.5 text-sm font-semibold text-gray-900">{f.value || '—'}</div>}
                    </label>
                ))}
            </div>
            <div className="mt-3 text-xs font-semibold text-gray-800">Papers attached on TARS</div>
            <ul className="mt-1 text-sm text-gray-800">
                {c.papers.map((p, i) => <li key={i}>{p.kind} · {p.name} · {kb(p.size)} · from {p.from}</li>)}
                {papers.map((p, i) => <li key={`n${i}`} className="text-purple-800">{p.name} · {kb(p.size)} · to send</li>)}
                {!c.papers.length && !papers.length && <li className="text-red-700">Ejar has no papers for this customer - add them below</li>}
            </ul>
            {waiting && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    <input type="file" multiple accept="image/*,.pdf" aria-label="Add papers" className="text-xs"
                        onChange={(e) => { setPapers([...papers, ...Array.from(e.target.files || [])]); e.target.value = ''; }} />
                    <span className="text-xs text-gray-600">the engine tells what each paper is and names it</span>
                    <Button className="ms-auto" onClick={send} disabled={sending} isLoading={sending}>
                        Continue on TARS
                    </Button>
                </div>
            )}
            {error && <div role="alert" className="mt-2 text-sm text-red-700">{error}</div>}
        </div>
    );
};

// Ejar has no pick-up odometer yet (the staff's Create Contract E-Check comes minutes after the contract): typed here
// instead of a stop, the car's last reading on Ejar already in the box and nothing lower taken (owner, 24 Oct 2026:
// "مربع الكيلومتر بالرقم الأخير للعربية مكتوب فيه جاهز، ومايقبلش رقم أقل منه"). It goes to TARS only.
const OdometerBox = ({ job, onStarted, busy }) => {
    const need = job.odometer_needed;
    const floor = need?.km || 0;
    const [value, setValue] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => { setValue(floor ? String(floor) : ''); setError(null); }, [job.job, floor]);
    if (!need) return null;
    const km = /^\d+$/.test(value.trim()) ? Number(value.trim()) : null;
    const tooLow = km !== null && km < floor;
    const send = async () => {
        setSending(true); setError(null);
        try { onStarted(await automationService.tarsRegisterOdometer(job.job, km)); } catch (e) { setError(e.message); }
        finally { setSending(false); }
    };
    return (
        <form className="mb-3 rounded-lg border border-amber-300 bg-amber-50/40 p-3"
            onSubmit={(e) => { e.preventDefault(); if (km && !tooLow) send(); }}>
            <div className="flex flex-wrap items-center gap-2">
                <label htmlFor="tars-odometer" className="text-sm font-semibold text-gray-900">Odometer at pick-up (km)</label>
                {need.plate && <span className="font-mono text-sm text-gray-800">{formatPlateNumber(need.plate)}</span>}
                <span className="w-36">
                    <Input id="tars-odometer" size="sm" inputMode="numeric" autoComplete="off" className="text-center font-mono"
                        value={value} onChange={(e) => setValue(e.target.value.replace(/[^\d]/g, ''))}
                        aria-invalid={tooLow || undefined} />
                </span>
                <Button type="submit" variant="primary" disabled={busy || sending || !km || tooLow} isLoading={sending}>
                    Continue on TARS
                </Button>
            </div>
            <div className={`mt-2 text-xs ${tooLow ? 'font-semibold text-red-700' : 'text-gray-700'}`}>
                {floor
                    ? <>The car&apos;s last reading on Ejar: <b>{floor.toLocaleString('en-US')} km</b>{need.seen ? ` · ${need.seen}` : ''} - a lower number is not taken.</>
                    : 'Ejar has no reading of this car yet - type the odometer from the car.'}
            </div>
            <div className="mt-1 text-xs text-gray-600">Ejar has none yet (the pick-up E-Check comes a few minutes after the contract). Typed here, it goes to TARS only - Ejar is not changed.</div>
            {error && <div role="alert" className="mt-2 text-sm text-red-700">{error}</div>}
        </form>
    );
};

const hhmm = (h, m) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

const TarsJob = ({ job, onCancel, cancelling, onJob, onCloseFirst, onStarted, busy }) => {
    const [open, setOpen] = useState(null);
    if (!job) return null;
    const shots = job.shots || [];
    const p = job.plan || {};
    const closing = job.mode === 'close';
    // the car is Rented on TARS under an earlier contract: one press closes that one (the Close on TARS job), then
    // registers this one again (owner, 14 Oct 2026: "لازم يكون فى زر ... يقفل العقد على تارز ويعيد تسجيل العقد الجديد")
    const blocker = !closing && job.state === 'stopped' && job.blocker && !job.blocker.unified ? job.blocker : null;
    const words = closing
        ? { ...JOB_WORDS, ready: 'filled - Close Contract not pressed', done: 'closed on TARS', already: 'not open on TARS - nothing done' }
        : JOB_WORDS;
    return (
        <Card className="mt-4">
            <div className="mb-2 flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900">TARS · {job.contract_no}</h3>
                <StatusBadge tone={JOB_TONE[job.state] || 'neutral'} label={words[job.state] || job.state} />
                {RUNNING.includes(job.state) && (
                    <span className="inline-flex items-center gap-2 text-xs text-gray-700">
                        <LoadingSpinner size="sm" />{job.step}
                    </span>
                )}
                {!job.submit_allowed && <Badge variant="warning">{closing ? 'Close Contract' : 'Submit'} is off until the trial is approved</Badge>}
                {['ready', ...RUNNING].includes(job.state) && !pressed(job) && (
                    <Button className="ms-auto" onClick={onCancel} disabled={cancelling} isLoading={cancelling}>
                        Cancel on TARS
                    </Button>
                )}
            </div>
            {job.error && (
                <div role="alert" className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    {job.error}
                </div>
            )}
            {blocker && (
                <div className="mb-2 flex flex-wrap items-center gap-2">
                    <Button variant="primary" disabled={busy} onClick={() => onCloseFirst(blocker.contract_no, job.contract_no)}>
                        Close {blocker.contract_no} on TARS, then register {job.contract_no}
                    </Button>
                    <span className="text-xs text-gray-600">closed from Ejar&apos;s return, like the Close on TARS tab - only when Ejar has closed it</span>
                </div>
            )}
            {!closing && job.state === 'stopped' && <OdometerBox job={job} onStarted={onStarted} busy={busy} />}
            {!!(job.notes || []).length && (
                <ul className="mb-2 list-disc ps-5 text-sm text-gray-700">
                    {job.notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
            )}
            {p.contract_no && closing && (
                <div className="mb-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-800 md:grid-cols-3">
                    <div>Car: <b>{p.plate_code} {p.plate_number}</b></div>
                    <div>Returned: <b>{p.checkin_date} {hhmm(p.checkin_hour, p.checkin_minute)}</b> · <b>{p.location}</b></div>
                    <div>Odometer: <b>{p.odometer_out ?? '?'} → {p.odometer}</b></div>
                    <div>Fuel at return: <b>{p.fuel_eighths}/8</b></div>
                </div>
            )}
            {p.contract_no && !closing && (
                <div className="mb-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-800 md:grid-cols-3">
                    <div>Car: <b>{p.plate_code} {p.plate_number}</b></div>
                    <div>Customer: <b>{p.identity}</b> · {p.phone}</div>
                    <div>Location: <b>{p.location}</b> · Employee: <b>{p.employee}</b></div>
                    <div>Start: <b>{p.start_date} {String(p.start_hour).padStart(2, '0')}:{String(p.start_minute).padStart(2, '0')}</b></div>
                    <div>Return: <b>{p.return_date} {String(p.return_hour).padStart(2, '0')}:{String(p.return_minute).padStart(2, '0')}</b></div>
                    <div>Odometer: <b>{p.odometer}</b>{p.odometer_typed && ' (typed)'} · Fuel: <b>{p.fuel_eighths}/8</b></div>
                </div>
            )}
            {!closing && <NewCustomer job={job} onJob={onJob} />}
            {!!shots.length && (
                <div className="flex flex-wrap gap-3">
                    {shots.map((s, i) => (
                        <button key={i} type="button" onClick={() => setOpen(open === i ? null : i)}
                            className={`rounded-lg border px-3 py-2 text-xs font-medium ${open === i ? 'border-purple-500 bg-purple-50 text-purple-800' : 'border-gray-300 bg-white text-gray-800'}`}>
                            {i + 1}. {s.step}
                        </button>
                    ))}
                </div>
            )}
            {open !== null && shots[open] && (
                <img className="mt-3 w-full rounded-lg border border-gray-200" alt={shots[open].step}
                    src={`data:image/jpeg;base64,${shots[open].jpeg}`} />
            )}
        </Card>
    );
};

// mode "register": Ejar's new contracts onto TARS. mode "close": a contract Ejar has closed is closed on TARS too, so
// the car is Available for its next contract (owner, 10 Oct 2026: "هنشتغل دلوقتى على قفل العقد على تارز") - TARS's
// Close Rental Contract screen typed from Ejar's return, Terms on, Close Contract NOT pressed.
export default function TarsRegisterTab({ mode = 'register' }) {
    const closing = mode === 'close';
    const [days, setDays] = useState(1);              // today's contracts first (owner, 10 Oct 2026)
    // a period of the employee's own, From / To, and only the contracts still to do (owner, 11 Oct 2026: "ازاى اعرف
    // العقود اللى متسجلتش لو حددت تاريخ من والى")
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [onlyMissing, setOnlyMissing] = useState(false);
    const [list, setList] = useState(null);
    const [loading, setLoading] = useState(true);    // the first read starts at once: no error frame before it
    const [notice, setNotice] = useState(null);
    const [job, setJob] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const [starting, setStarting] = useState(false);
    const [number, setNumber] = useState('');
    // the employee's own papers instead of Ejar's scans (owner, 10 Oct 2026: "نعمل زر مربع لو دوست عليها يتفعل
    // وارفعله الاوراق من عندى ويستخدمها ولو مرفعتش يستخدم اوراق العميل من ايجار"); a customer already on TARS keeps
    // TARS's own papers (the owner's choice 1)
    const [own, setOwn] = useState(false);
    const [ownPapers, setOwnPapers] = useState([]);
    const watching = useRef(null);

    // only the latest read lands: stepping through the dates fires several reads, and an older answer must not
    // replace the list of the period now picked (review 18 Oct 2026)
    const loadSeq = useRef(0);
    const load = useCallback(async () => {
        const seq = ++loadSeq.current;
        setLoading(true); setNotice(null);
        try {
            const got = await (closing ? automationService.tarsCloseContracts(days, from, to) : automationService.tarsContracts(days, from, to));
            if (seq === loadSeq.current) setList(got);
        } catch (e) { if (seq === loadSeq.current) { setList(null); setNotice(e.message); } }
        finally { if (seq === loadSeq.current) setLoading(false); }
    }, [days, closing, from, to]);
    useEffect(() => { load(); }, [load]);

    // a job started before a tab switch is picked up again
    useEffect(() => {
        const id = remembered(mode);
        if (!id) return;
        automationService.tarsRegisterProgress(id, true)
            .then((j) => { if (LIVE.includes(j?.state) || pressed(j)) setJob(j); else remember(mode, null); })
            .catch(() => remember(mode, null));
    }, [mode]);

    // follow a running job: the step names while it runs (light), the pictures once it has stopped
    useEffect(() => {
        if (!job?.job || !RUNNING.includes(job.state)) return undefined;
        let alive = true;
        watching.current = setTimeout(async () => {
            try {
                const light = await automationService.tarsRegisterProgress(job.job, false);
                if (!alive) return;
                if (light.state === 'unknown') { remember(mode, null); setJob({ ...job, state: 'expired' }); return; }
                setJob(RUNNING.includes(light.state) ? { ...light, shots: job.shots }
                    : await automationService.tarsRegisterProgress(job.job, true));
            } catch (e) {
                if (alive) { setNotice(`${e.message} - trying again`); setJob({ ...job }); }   // a failed check is retried
            }
        }, 2500);
        return () => { alive = false; clearTimeout(watching.current); };
    }, [job, mode]);

    // a job that has ended leaves: cancelled or done ones after a moment (the list is read again), and none of them
    // comes back after a refresh (owner, 10 Oct 2026: "المفروض كنسلت او كملت تختفى اتوماتيك")
    useEffect(() => {
        if (!job?.job || LIVE.includes(job.state)) return undefined;
        if (!pressed(job)) remember(mode, null);   // "pressed, not seen on TARS yet" stays, refresh or not
        if (!GONE.includes(job.state)) return undefined;
        const t = setTimeout(() => { setJob(null); load(); }, 5000);
        return () => clearTimeout(t);
    }, [job, mode, load]);

    // after: the contract registered again once this close is done (the car Rented under an earlier TARS contract);
    // afterKm: the odometer typed for it on its card, registered with it again (review, 24 Oct 2026)
    const [after, setAfter] = useState(null);
    const afterKm = useRef(null);
    // what was done on TARS, kept after the card leaves (owner, 14 Oct 2026: "مجابليش اشعار نجاح فى الاغلاق او
    // التسجيل") - a close-first shows both lines; a new job started by hand starts a new list
    const [doneLines, setDoneLines] = useState([]);
    const told = useRef(new Set());
    useEffect(() => {
        if (!job?.job || !['done', 'already'].includes(job.state) || told.current.has(job.job)) return;
        told.current.add(job.job);
        const what = job.mode === 'close'
            ? (job.state === 'done' ? 'closed on TARS' : 'was not open on TARS - nothing to close')
            : (job.state === 'done' ? 'registered on TARS' : 'was on TARS already - nothing done');
        setDoneLines((was) => [...was, `${job.contract_no} ${what}`]);
    }, [job]);

    const register = async (contractNo, how = '') => {
        setNotice(null); setStarting(true);
        if (how !== 'again') setDoneLines([]);
        try {
            const papers = !closing && own && !how;
            if (papers && !ownPapers.length) { setNotice('Add your papers, or untick "Use my papers"'); return; }
            const started = closing || how === 'close' ? await automationService.tarsClose(contractNo)
                : papers ? await automationService.tarsRegisterWithPapers(contractNo, ownPapers)
                    : await automationService.tarsRegister(contractNo, how === 'again' ? afterKm.current : null);
            remember(mode, started.job);
            setJob(started);
            if (papers) { setOwn(false); setOwnPapers([]); }
            return started;
        } catch (e) { setNotice(e.message); return null; }
        finally { setStarting(false); }
    };

    // a staff press writes on TARS (a government system): asked first, naming the contract (review 18 Oct 2026)
    const askThenRegister = async (contractNo, row = null) => {
        const who = row ? [formatPlateNumber(row.plate), row.customer_name].filter(Boolean).join(' · ') : '';
        const ok = await confirmDialog({
            title: `${closing ? 'Close' : 'Register'} ${contractNo} on TARS?`,
            message: (who ? `${who}\n\n` : '')
                + (closing ? "The engine fills TARS's close screen from Ejar's return and presses Close Contract."
                    : "The engine fills TARS's five screens from Ejar and presses Submit."),
            confirmLabel: closing ? 'Close on TARS' : 'Register on TARS',
            danger: closing,
        });
        if (ok) register(contractNo);
    };

    // the "register again in 15 s" after a close: dropped when the tab is left in the meantime (review 18 Oct 2026)
    const againTimer = useRef(null);
    useEffect(() => () => { if (againTimer.current) clearTimeout(againTimer.current); }, []);

    const closeFirst = async (earlier, contractNo) => {
        afterKm.current = job?.contract_no === contractNo && job?.plan?.odometer_typed ? job.plan.odometer : null;
        setAfter(contractNo);
        if (!(await register(earlier, 'close'))) setAfter(null);      // the close did not even start
    };

    // the earlier contract closed on TARS: this one is registered again; not closed: said, nothing registered
    useEffect(() => {
        if (!after || job?.mode !== 'close' || LIVE.includes(job.state)) return;
        setAfter(null);
        if (!['done', 'already'].includes(job.state)) {
            setNotice(`${job.contract_no} was not closed on TARS - ${after} was not registered`);
            return;
        }
        // TARS's car list shows the car Available a few seconds after the close; not tied to the card, which leaves
        setNotice(`${job.contract_no} closed on TARS - registering ${after} in a moment`);
        setStarting(true);                         // busy meanwhile: no other job started in the gap (review)
        againTimer.current = setTimeout(() => { againTimer.current = null; register(after, 'again'); }, 15000);
    }, [job, after]); // eslint-disable-line react-hooks/exhaustive-deps

    const cancel = async () => {
        if (!job?.job) return;
        setCancelling(true);
        try {
            await automationService.tarsRegisterCancel(job.job);
            setJob(await automationService.tarsRegisterProgress(job.job, true));
            load();
        } catch (e) { setNotice(e.message); }
        finally { setCancelling(false); }
    };

    const all = list?.contracts || [];
    const toDo = (r) => (closing ? r.tars_status === 'ACTIVE' : r.on_tars === false);
    const missing = all.filter(toDo).length;
    const rows = onlyMissing ? all.filter(toDo) : all;
    const ranged = !!(from || to);
    const action = closing ? 'Close on TARS' : 'Register on TARS';
    const busy = starting || (!!job && RUNNING.includes(job.state));

    return (
        <div className="space-y-4">
            <Card>
                <div className="flex flex-wrap items-center gap-3">
                    <h2 className="text-sm font-semibold text-gray-900">{closing ? 'Close contracts on TARS' : 'Register contracts on TARS'}</h2>
                    {list?.session?.present
                        ? <StatusBadge tone="success" label={`engine signed in to TARS ${list.session.importedAt || ''}`} />
                        : <StatusBadge tone="warning" label="no TARS session yet - the engine signs in when needed" />}
                    {list?.tars_error && <Badge variant="danger">TARS: {list.tars_error}</Badge>}
                    <span className="ms-auto inline-flex items-center gap-2">
                        <label htmlFor="tars-days" className="whitespace-nowrap text-xs text-gray-700">{closing ? 'closed' : 'opened'} in the last</label>
                        <span className="w-28">
                            <Select id="tars-days" size="sm" value={days} disabled={ranged} onChange={(e) => setDays(Number(e.target.value))}>
                                {[1, 3, 7, 14].map((d) => <option key={d} value={d}>{d} day{d > 1 ? 's' : ''}</option>)}
                            </Select>
                        </span>
                        <Button leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />} onClick={load} disabled={loading} isLoading={loading}>
                            Refresh
                        </Button>
                    </span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-200 pt-3">
                    <label htmlFor="tars-from" className="text-xs font-medium text-gray-800">From</label>
                    <span className="w-40"><Input id="tars-from" type="date" size="sm" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} /></span>
                    <label htmlFor="tars-to" className="text-xs font-medium text-gray-800">To</label>
                    <span className="w-40"><Input id="tars-to" type="date" size="sm" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} /></span>
                    {ranged && <Button size="sm" onClick={() => { setFrom(''); setTo(''); }}>Clear dates</Button>}
                    <label className="ms-2 inline-flex items-center gap-2 text-sm font-medium text-gray-900">
                        <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} />
                        {closing ? 'Only those still open on TARS' : 'Only those not on TARS'}
                    </label>
                    {list?.from && <span className="text-xs text-gray-600">{closing ? 'closed' : 'opened'} {list.from === list.to ? `on ${list.from}` : `${list.from} → ${list.to}`}</span>}
                </div>
                <div className="mt-2 text-xs text-gray-600">
                    {closing
                        ? <>The engine fills TARS&apos;s Close Rental Contract screen from Ejar&apos;s return, turns Terms on, <b>presses Close Contract</b> and reads TARS until the contract shows closed.</>
                        : <>The engine fills TARS&apos;s five screens from Ejar, turns Terms on, <b>presses Submit</b> and reads TARS until the contract is there.</>}
                    {list && <> · {all.length} contracts · <b>{missing}</b> {closing ? 'still open on TARS' : 'not on TARS'}</>}
                </div>
            </Card>

            {/* type a contract number: the engine reads it from Ejar (even one opened a minute ago) and fills TARS up to
                the last step (owner, 10 Oct 2026: "اعمل مربع بحث احط فيه رقم العقد يجيبه من ايجار ويسجله ويقف على الخطوة الاخيرة") */}
            <Card>
                <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => {
                    e.preventDefault();
                    const n = number.trim().toUpperCase();
                    if (n) askThenRegister(n);
                }}>
                    <label htmlFor="tars-number" className="text-sm font-semibold text-gray-900">Contract number</label>
                    <Input id="tars-number" className="w-72 font-mono uppercase" placeholder="I2610000D01507944"
                        value={number} onChange={(e) => setNumber(e.target.value)} autoComplete="off" />
                    <Button type="submit" variant="primary" disabled={busy || !number.trim()} isLoading={starting}>
                        {action}
                    </Button>
                    <span className="text-xs text-gray-600">
                        {closing ? "Ejar's return typed on TARS's close screen, then Close Contract pressed"
                            : 'read from Ejar, filled on TARS, then Submit pressed'}
                    </span>
                </form>
                {!closing && <div className="mt-3 border-t border-gray-200 pt-3">
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-900">
                        <input type="checkbox" checked={own} onChange={(e) => setOwn(e.target.checked)} />
                        Use my papers instead of Ejar&apos;s
                    </label>
                    <span className="ms-2 text-xs text-gray-600">
                        {own ? 'for the next Register only - used when the customer is new on TARS; the engine tells what each paper is and names it'
                            : "unticked: the scans on the customer's Ejar profile are attached"}
                    </span>
                    {own && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <input type="file" multiple accept="image/*,.pdf" aria-label="Add papers" className="text-xs"
                                onChange={(e) => { setOwnPapers([...ownPapers, ...Array.from(e.target.files || [])]); e.target.value = ''; }} />
                            {ownPapers.map((p, i) => (
                                <span key={i} className="inline-flex items-center gap-1 rounded-full border border-purple-300 bg-purple-50 px-2 py-0.5 text-xs text-purple-900">
                                    {p.name} · {kb(p.size)}
                                    <button type="button" aria-label={`Remove ${p.name}`} className="font-bold text-purple-700"
                                        onClick={() => setOwnPapers(ownPapers.filter((_, j) => j !== i))}>×</button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>}
            </Card>

            {!!doneLines.length && (
                <div role="status" className="rounded-lg border border-green-300 bg-green-50 px-4 py-2 text-sm font-medium text-green-900">
                    {doneLines.map((l, i) => <div key={i}>✓ {l}</div>)}
                </div>
            )}
            {notice && (
                <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">{notice}</div>
            )}

            <TarsJob job={job} onCancel={cancel} cancelling={cancelling} busy={busy}
                onCloseFirst={closeFirst}
                onStarted={(started) => { remember(mode, started.job); setNotice(null); setJob(started); }}
                onJob={(j) => setJob((was) => ({ ...j, shots: was?.shots }))} />

            <Card>
                {loading && !list ? (
                    <div className="inline-flex items-center gap-2 text-sm text-gray-700"><LoadingSpinner size="sm" />reading Ejar and TARS…</div>
                ) : !list ? (
                    // a failed read is never shown as "no contracts" - staff would think nothing waits for TARS
                    <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-red-700">
                        <Icon glyph={ICONS.alert} size="sm" tone="danger" />
                        <span>The contracts could not be read{notice ? `: ${notice}` : ''}</span>
                        <Button size="sm" variant="secondary" onClick={load}>Try again</Button>
                    </div>
                ) : (
                    <TableContainer>
                        <Table sticky>
                            <TableHead>
                                <TableRow>
                                    {['#', 'Contract', closing ? 'Closed' : 'Opened', 'Branch', 'Car', 'Customer', 'Ejar', 'On TARS', ''].map((h) => (
                                        <TableHeader key={h} align="center">{h}</TableHeader>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r, i) => {
                                    return (
                                        <TableRow key={r.contract_no}>
                                            <TableCell align="center">{i + 1}</TableCell>
                                            <TableCell align="center" className="font-mono">{r.contract_no}</TableCell>
                                            <TableCell align="center">{closing ? r.closed : r.opened}</TableCell>
                                            <TableCell align="center">{BRANCH[r.branch_id] || r.branch_id}</TableCell>
                                            <TableCell align="center" className="font-mono">{formatPlateNumber(r.plate)}</TableCell>
                                            <TableCell align="center">{r.customer_name}</TableCell>
                                            <TableCell align="center">{closing ? r.status : r.status_norm}</TableCell>
                                            {closing ? (
                                                <TableCell align="center">
                                                    {list?.tars_error ? '—'
                                                        : r.tars_status === 'ACTIVE' ? <Badge variant="warning">open on TARS</Badge>
                                                            : r.tars_status ? <Badge variant="success">{r.tars_status.toLowerCase()}</Badge>
                                                                : <Badge variant="danger">not on TARS</Badge>}
                                                    {r.tars_number && <CellMuted>as {r.tars_number}</CellMuted>}
                                                </TableCell>
                                            ) : (
                                            <TableCell align="center">
                                                {r.on_tars === null ? '—'
                                                    : r.on_tars ? <Badge variant="success">on TARS</Badge>
                                                        : <Badge variant="danger">not on TARS</Badge>}
                                                {/* staff typed the number otherwise on TARS ("B...516x"): the same
                                                    contract, shown as TARS holds it (owner, 19 Oct 2026) */}
                                                {r.tars_number && <CellMuted>as {r.tars_number}</CellMuted>}
                                            </TableCell>
                                            )}
                                            <TableCell align="center">
                                                {/* a company's contract is a B2C one too: one way for everybody (owner, 10 Oct 2026) */}
                                                {closing && (r.tars_status === 'ACTIVE' || list?.tars_error) && (
                                                    <Button size="sm" variant="primary" disabled={busy} onClick={() => askThenRegister(r.contract_no, r)}>
                                                        Close on TARS
                                                    </Button>
                                                )}
                                                {!closing && (r.on_tars === false || (r.on_tars === null && list?.tars_error)) && (
                                                    !r.location ? <span className="text-xs text-gray-600">branch not mapped</span>
                                                        : (
                                                            <Button size="sm" variant="primary" disabled={busy}
                                                                onClick={() => askThenRegister(r.contract_no, r)}>
                                                                Register on TARS
                                                            </Button>
                                                        )
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {!rows.length && (
                                    <TableRow><TableCell align="center" colSpan={9}>{onlyMissing && all.length ? (closing ? 'every contract of this period is closed on TARS' : 'every contract of this period is on TARS') : `no contracts ${closing ? 'closed' : 'opened'} in this period`}</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Card>
        </div>
    );
}
